import React, { useState, useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { FileText, Download, CheckCircle2, ChevronLeft, ChevronRight, Loader2, Edit, X, Send } from 'lucide-react';
import { useGenerateInvoicesMutation, useGetUngeneratedInvoicesQuery, useGetLandlordInvoicesQuery, useDeleteInvoiceMutation, useAddCreditNoteMutation, useDeleteKraInvoiceMutation, useSendInvoiceSmsMutation } from '../../lib/apiSlice/invoicing/invoicingSlice.tsx';
import { toast } from 'react-toastify';

export default function InvoicingTable({ month, year, onMonthChange, onYearChange }) {
    const [filteredHouses, setFilteredHouses] = useState([]);
    const [allHouses, setAllHouses] = useState([]);
    const [selectedTenants, setSelectedTenants] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [apartmentFilter, setApartmentFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [invoiceEdits, setInvoiceEdits] = useState({});
    const itemsPerPage = 10;
    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' }).toLowerCase();
    const currentYear = currentDate.getFullYear().toString();
    const [localMonth, setLocalMonth] = useState(month || currentMonth);
    const [localYear, setLocalYear] = useState(year || currentYear);
    const selectedMonth = month || localMonth;
    const selectedYear = year || localYear;
    const setSelectedMonth = onMonthChange || setLocalMonth;
    const setSelectedYear = onYearChange || setLocalYear;
    const [isGenerating, setIsGenerating] = useState(false);
    const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
    const [invoiceVarianceFilter, setInvoiceVarianceFilter] = useState('all');
    const [consolidateInvoices, setConsolidateInvoices] = useState(false);
    const [sendSmsOnGenerate, setSendSmsOnGenerate] = useState(false);
    const [invoicePage, setInvoicePage] = useState(1);
    const invoiceItemsPerPage = 10;
    const [selectedExistingInvoices, setSelectedExistingInvoices] = useState([]);
    const [showInvoiceSmsModal, setShowInvoiceSmsModal] = useState(false);
    const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
    const [selectedCreditInvoice, setSelectedCreditInvoice] = useState(null);
    const [creditAmount, setCreditAmount] = useState('');
    const [creditReasonCode, setCreditReasonCode] = useState('02');
    const [adjustmentType, setAdjustmentType] = useState('overstatement');
    const [creditNoteResult, setCreditNoteResult] = useState(null);
    const creditReasonOptions = [
        { value: '01', label: '01 - Missing Quantity' },
        { value: '02', label: '02 - Missing Data' },
        { value: '03', label: '03 - Damaged' },
        { value: '04', label: '04 - Wasted' },
        { value: '05', label: '05 - Raw Material Shortage' },
        { value: '06', label: '06 - Refund' },
    ];

    const monthOptions = [
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'
    ];
    const yearOptions = Array.from({ length: 5 }, (_, i) => (parseInt(currentYear, 10) - 2 + i).toString());

    const buildInvoiceOptions = (tenant) => {
        const options = [];
        if (tenant.can_add_rent) {
            options.push({
                value: 'rent',
                label: 'Rent',
                reference: 'rent',
                description: 'rent',
                amount: tenant.rent_amount ?? tenant.rent ?? 0
            });
        }
        (tenant.available_expenses || []).forEach((expense) => {
            const amount = expense.amount ?? expense.cost_per_unit ?? 0;
            const label = expense.expense_type === 'varying' && expense.units_consumed
                ? `${expense.title} (variable, ${expense.units_consumed} units)`
                : `${expense.title} (${expense.expense_type})`;
            options.push({
                value: `expense:${expense.id}`,
                label,
                reference: 'expense',
                description: String(expense.id),
                amount
            });
        });
        return options;
    };

    const getAvailableExpensesTotal = (tenant) => {
        return (tenant.available_expenses || []).reduce((sum, exp) => {
            const amount = exp.amount ?? exp.cost_per_unit ?? 0;
            return sum + Number(amount || 0);
        }, 0);
    };

    const getConsolidatedAmount = (tenant) => {
        const rentAmount = tenant.rent_amount ?? tenant.rent ?? 0;
        return Number(rentAmount) + getAvailableExpensesTotal(tenant);
    };

    useEffect(() => {
        if (month) {
            setLocalMonth(month);
        }
    }, [month]);

    useEffect(() => {
        if (year) {
            setLocalYear(year);
        }
    }, [year]);

    const { data: invoiceData, isLoading, error } = useGetUngeneratedInvoicesQuery({
        month: selectedMonth,
        year: selectedYear
    });
    const { data: landlordInvoices, isLoading: isInvoicesLoading, error: invoicesError } = useGetLandlordInvoicesQuery({
        month: selectedMonth,
        year: selectedYear
    });
    const uniqueApartments = [...new Set(
        invoiceData?.pending_invoices?.map(invoice => invoice.apartment) || []
    )];

  useEffect(() => {
    if (invoiceData?.pending_invoices) {
        const transformedData = invoiceData.pending_invoices.map(invoice => ({
            id: invoice.house_id,
            house_number: invoice.house_number,
            apartment: invoice.apartment,
            apartment_id: String(invoice.apartment_id),
            tenant_name: invoice.tenant_name,
            phone_number: invoice.phone_number,
            rent: invoice.rent,
            rent_amount: invoice.rent_amount,
            user_instance: invoice.user_instance,
            balance: invoice.balance,
            can_add_rent: invoice.can_add_rent,
            available_expenses: invoice.available_expenses || [],
        }));

        setAllHouses(transformedData);
        setFilteredHouses(transformedData);

        // Initialize or preserve existing edits
        setInvoiceEdits(prevEdits => {
            const newEdits = {};
            transformedData.forEach(tenant => {
                const options = buildInvoiceOptions(tenant);
                const defaultOption = options[0];
                if (prevEdits[tenant.id]) {
                    newEdits[tenant.id] = {
                        type: prevEdits[tenant.id].type || defaultOption?.value,
                        amount: prevEdits[tenant.id].amount ?? defaultOption?.amount ?? tenant.rent_amount ?? tenant.rent ?? 0,
                        reference: prevEdits[tenant.id].reference || defaultOption?.reference,
                        description: prevEdits[tenant.id].description || defaultOption?.description
                    };
                } else {
                    newEdits[tenant.id] = {
                        type: defaultOption?.value,
                        amount: defaultOption?.amount ?? tenant.rent_amount ?? tenant.rent ?? 0,
                        reference: defaultOption?.reference,
                        description: defaultOption?.description
                    };
                }
            });
            return newEdits;
        });
    }
}, [invoiceData]);

    // Filter logic
    useEffect(() => {
        let results = allHouses.filter(h =>
            (!apartmentFilter || h.apartment === apartmentFilter) &&
            (!searchTerm || Object.values(h).some(val =>
                val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            ))
        );
        setFilteredHouses(results);
        setCurrentPage(1);
    }, [searchTerm, apartmentFilter, allHouses]);

    const handleTenantSelect = (tenantId) => {
        setSelectedTenants(prev =>
            prev.includes(tenantId) ? prev.filter(id => id !== tenantId) : [...prev, tenantId]
        );
    };

    const handleSelectAll = () => {
        const selectableIds = filteredHouses
            .filter(item => buildInvoiceOptions(item).length > 0)
            .map(item => item.id);
        if (selectedTenants.length === selectableIds.length) {
            setSelectedTenants([]);
        } else {
            setSelectedTenants(selectableIds);
        }
    };

    const [handleAddInvoice] = useGenerateInvoicesMutation();
    const [sendInvoiceSms, { isLoading: isSendingInvoiceSms }] = useSendInvoiceSmsMutation();
    const [deleteInvoice] = useDeleteInvoiceMutation();
    const [deleteKraInvoice] = useDeleteKraInvoiceMutation();
    const [addCreditNote, { isLoading: isPostingCreditNote }] = useAddCreditNoteMutation();

    const filteredInvoiceRows = (landlordInvoices?.results || []).filter((inv) => {
        const needle = invoiceSearchTerm.toLowerCase();
        const matchesSearch = !invoiceSearchTerm || [
            inv.house_number,
            inv.tenant_name,
            inv.tenant_phone,
            inv.apartment_name,
            inv.reference,
            inv.expense_name
        ].some((val) => val?.toString().toLowerCase().includes(needle));
        if (!matchesSearch) return false;

        const understatement = Number(inv?.understatement_amount || 0);
        const overstatement = Number(inv?.overstatement_amount || 0);

        if (invoiceVarianceFilter === 'understated') return inv?.discrepancy_direction === 'understated' && !inv?.is_voided;
        if (invoiceVarianceFilter === 'overstated') return inv?.discrepancy_direction === 'overstated' && !inv?.is_voided;
        if (invoiceVarianceFilter === 'balanced') return inv?.has_kra_invoice && !inv?.requires_void_and_reissue && understatement <= 0 && overstatement <= 0;
        if (invoiceVarianceFilter === 'no_kra') return !inv?.has_kra_invoice;
        return true;
    });

    const renderInvoiceVariance = (invoice) => {
        const currentKraAmount = Number(invoice?.kra_invoice_amount || 0);
        const replacementAmount = Number(invoice?.replacement_amount || invoice?.expected_etims_amount || 0);

        if (invoice?.is_voided) {
            return (
                <span className="text-slate-700 text-xs">
                    Current KRA invoice voided. Replacement amount: KSH {replacementAmount.toLocaleString()}
                </span>
            );
        }

        if (invoice?.requires_void_and_reissue) {
            const tone = invoice?.discrepancy_direction === 'overstated' ? 'text-rose-700' : 'text-amber-700';
            const label = invoice?.discrepancy_direction === 'overstated' ? 'Overstated KRA invoice' : 'Understated KRA invoice';
            return (
                <span className={`${tone} text-xs`}>
                    {label}. Void KSH {currentKraAmount.toLocaleString()} and reissue KSH {replacementAmount.toLocaleString()}.
                </span>
            );
        }

        if (Number(invoice?.understatement_amount || 0) > 0) {
            return (
                <span className="text-amber-700 text-xs">
                    Understated by KSH {Number(invoice.understatement_amount || 0).toLocaleString()}
                </span>
            );
        }

        if (Number(invoice?.overstatement_amount || 0) > 0) {
            return (
                <span className="text-rose-700 text-xs">
                    Overstated by KSH {Number(invoice.overstatement_amount || 0).toLocaleString()}
                </span>
            );
        }

        return null;
    };

    const canSendInvoiceSms = (invoice) => Boolean(invoice?.id && invoice?.tenant_phone);

    const getInvoiceSmsLabel = (invoice) => {
        if (invoice?.reference === 'expense') {
            return 'utility';
        }
        return invoice?.reference || 'invoice';
    };

    const formatInvoiceMonth = (monthValue) => {
        if (!monthValue) return '';
        return monthValue.charAt(0).toUpperCase() + monthValue.slice(1);
    };

    const buildInvoiceSmsPreview = (invoice) => {
        const tenantName = invoice?.tenant_name || 'Tenant';
        const invoiceLabel = getInvoiceSmsLabel(invoice);
        const unitLabel = [invoice?.house_number, invoice?.apartment_name].filter(Boolean).join(' ') || 'your unit';
        const monthLabel = formatInvoiceMonth(invoice?.month);

        return `Hello ${tenantName},
your ${invoiceLabel} invoice for ${unitLabel} for ${monthLabel}, ${invoice?.year} is ready. Please click the link below to download your PDF invoice.

The invoice download link will be included automatically. [Kodi Powered by Jumuisha Ltd]`;
    };

    const invoiceTotalPages = Math.ceil(filteredInvoiceRows.length / invoiceItemsPerPage);
    const invoiceIndexOfLast = invoicePage * invoiceItemsPerPage;
    const invoiceIndexOfFirst = invoiceIndexOfLast - invoiceItemsPerPage;
    const invoiceCurrentItems = filteredInvoiceRows.slice(invoiceIndexOfFirst, invoiceIndexOfLast);
    const allInvoiceRows = landlordInvoices?.results || [];
    const selectedExistingInvoiceRows = allInvoiceRows.filter((invoice) => selectedExistingInvoices.includes(invoice.id));
    const selectableFilteredInvoiceIds = filteredInvoiceRows
        .filter(canSendInvoiceSms)
        .map((invoice) => invoice.id);
    const allFilteredInvoicesSelected = selectableFilteredInvoiceIds.length > 0
        && selectableFilteredInvoiceIds.every((invoiceId) => selectedExistingInvoices.includes(invoiceId));

    const handleGenerateInvoices = async () => {
        setIsGenerating(true);
        const smsRequested = sendSmsOnGenerate;

        // Get all selected tenant data with their edits
        const selectedTenantData = filteredHouses
            .filter(house => selectedTenants.includes(house.id))
            .map(tenant => {
                if (consolidateInvoices && tenant.can_add_rent) {
                    return {
                        ...tenant,
                        invoiceType: 'rent',
                        invoiceAmount: getConsolidatedAmount(tenant),
                        invoiceReference: 'rent',
                        invoiceDescription: 'rent'
                    };
                }
                return {
                    ...tenant,
                    invoiceType: invoiceEdits[tenant.id]?.type,
                    invoiceAmount: invoiceEdits[tenant.id]?.amount ?? tenant.rent_amount ?? tenant.rent ?? 0,
                    invoiceReference: invoiceEdits[tenant.id]?.reference,
                    invoiceDescription: invoiceEdits[tenant.id]?.description
                };
            })
            .filter(item => item.invoiceReference);
        if (selectedTenantData.length === 0) {
            setIsGenerating(false);
            toast.error("No valid invoice types selected.");
            return;
        }

        try {
            let successCount = 0;
            let failureCount = 0;

            for (const tenant of selectedTenantData) {
                const invoiceData = {
                    user: tenant.user_instance,
                    house: tenant.id,
                    month: selectedMonth,
                    amount: tenant.invoiceAmount,
                    year: selectedYear,
                    status: 'unpaid',
                    reference: tenant.invoiceReference,
                    description: tenant.invoiceDescription,
                    send_sms: smsRequested,
                };

                try {
                    await handleAddInvoice(invoiceData).unwrap();
                    successCount += 1;
                } catch (error) {
                    failureCount += 1;
                    continue;
                }
            }

            setShowModal(false);
            setIsGenerating(false);
            setSelectedTenants([]);
            setSendSmsOnGenerate(false);

            if (successCount > 0) {
                toast.success(
                    `${successCount} invoice(s) generated successfully${smsRequested ? ' and queued for SMS notification' : ''}${failureCount > 0 ? ` (${failureCount} failed)` : ''}.`,
                    {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    }
                );
            }

            if (failureCount > 0) {
                toast.error(`${failureCount} invoice(s) failed to generate.`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }

        } catch (error) {
            setIsGenerating(false);
            setSendSmsOnGenerate(false);
            toast.error(`Error generating invoices: ${error.message}`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    };

    const handleDeleteInvoice = async (invoiceId) => {
        try {
            await deleteInvoice(invoiceId).unwrap();
            toast.success("Invoice deleted successfully.");
        } catch (err) {
            toast.error("Failed to delete invoice.");
        }
    };

    const handleExistingInvoiceSelect = (invoiceId) => {
        setSelectedExistingInvoices((prev) => (
            prev.includes(invoiceId)
                ? prev.filter((id) => id !== invoiceId)
                : [...prev, invoiceId]
        ));
    };

    const handleSelectAllExistingInvoices = () => {
        setSelectedExistingInvoices((prev) => {
            if (allFilteredInvoicesSelected) {
                return prev.filter((invoiceId) => !selectableFilteredInvoiceIds.includes(invoiceId));
            }
            return [...new Set([...prev, ...selectableFilteredInvoiceIds])];
        });
    };

    const handleOpenInvoiceSmsModal = (invoiceIds = selectedExistingInvoices) => {
        if (!invoiceIds.length) {
            toast.error("Select at least one generated invoice with a phone number.");
            return;
        }
        setSelectedExistingInvoices(invoiceIds);
        setShowInvoiceSmsModal(true);
    };

    const handleSendExistingInvoiceSms = async () => {
        if (selectedExistingInvoiceRows.length === 0) {
            toast.error("No generated invoices selected for SMS.");
            return;
        }

        try {
            const response = await sendInvoiceSms({
                invoice_ids: selectedExistingInvoiceRows.map((invoice) => invoice.id),
            }).unwrap();

            const sentCount = response?.sent_count ?? 0;
            const skippedCount = response?.skipped_count ?? 0;
            const failedCount = response?.failed_count ?? 0;
            const skippedReasons = (response?.results || [])
                .map((result) => result?.sms_result?.reason || result?.reason)
                .filter(Boolean);

            if (sentCount > 0) {
                toast.success(`${sentCount} invoice SMS notification(s) sent successfully.`);
            }

            if (skippedCount > 0) {
                const debugSkip = skippedReasons.includes('debug_mode');
                toast.info(
                    `${skippedCount} invoice SMS notification(s) were skipped${debugSkip ? ' because DEBUG mode is enabled.' : '.'}`
                );
            }

            if (failedCount > 0) {
                toast.error(`${failedCount} invoice SMS notification(s) failed.`);
            }

            setShowInvoiceSmsModal(false);
            setSelectedExistingInvoices([]);
        } catch (error) {
            const errorMessage = error?.data || error?.message || "Failed to send invoice SMS.";
            toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to send invoice SMS.");
        }
    };

    const handleDeleteKraInvoice = async (invoiceId) => {
        try {
            const response = await deleteKraInvoice(invoiceId).unwrap();
            toast.success(response?.message || "KRA invoice deleted successfully.");
        } catch (err) {
            const errorMessage = err?.data || err?.message || "Failed to delete KRA invoice.";
            toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to delete KRA invoice.");
        }
    };

    const openCreditNoteModal = (invoice) => {
        const currentKraAmount = Number(invoice?.kra_invoice_amount || 0);
        const suggestedAmount = currentKraAmount > 0
            ? currentKraAmount
            : Number(invoice?.amount || 0);
        const suggestedAdjustmentType = 'overstatement';

        setSelectedCreditInvoice(invoice);
        setCreditAmount(String(suggestedAmount || ''));
        setCreditReasonCode('02');
        setAdjustmentType(suggestedAdjustmentType);
        setCreditNoteResult(null);
        setShowCreditNoteModal(true);
    };

    const handleCreateCreditNote = async () => {
        if (!selectedCreditInvoice?.id) {
            toast.error("No invoice selected.");
            return;
        }

        const parsedAmount = Number(creditAmount);
        if (!parsedAmount || parsedAmount <= 0) {
            toast.error("Enter a valid credit amount.");
            return;
        }

        try {
            const response = await addCreditNote({
                adjustment_type: adjustmentType,
                credit_note_reason_code: creditReasonCode,
                credits: [
                    {
                        invoice_id: selectedCreditInvoice.id,
                        credit_amount: parsedAmount,
                        adjustment_type: adjustmentType
                    }
                ]
            }).unwrap();
            setCreditNoteResult(response);

            const failedCount = response?.failed_count ?? response?.failed?.length ?? 0;
            const createdCount = response?.created_count ?? response?.created?.length ?? 0;

            if (failedCount > 0) {
                const firstError = response?.failed?.[0]?.error || "Failed to create credit note.";
                toast.error(firstError);
                return;
            }

            const createdRecord = response?.created?.[0];
            toast.success(
                `Credit note created (${createdCount}). Number: ${createdRecord?.credit_invoice_number || 'N/A'}`
            );
        } catch (err) {
            toast.error("Failed to create credit note.");
        }
    };

    useEffect(() => {
        setInvoicePage(1);
    }, [invoiceSearchTerm, invoiceVarianceFilter, selectedMonth, selectedYear]);

    useEffect(() => {
        const availableInvoiceIds = new Set((landlordInvoices?.results || []).map((invoice) => invoice.id));
        setSelectedExistingInvoices((prev) => prev.filter((invoiceId) => availableInvoiceIds.has(invoiceId)));
    }, [landlordInvoices]);

    const handleEditInvoice = (tenantId) => {
        setEditingInvoice(tenantId);
    };

    const handleCancelEdit = () => {
        setEditingInvoice(null);
    };

    const handleInvoiceTypeChange = (tenantId, type, options) => {
        const selected = options.find(opt => opt.value === type);
        setInvoiceEdits(prev => ({
            ...prev,
            [tenantId]: {
                ...prev[tenantId],
                type,
                reference: selected?.reference,
                description: selected?.description,
                amount: selected?.amount ?? prev[tenantId]?.amount ?? 0
            }
        }));
    };

    const handleAmountChange = (tenantId, amount) => {
        setInvoiceEdits(prev => ({
            ...prev,
            [tenantId]: {
                ...prev[tenantId],
                amount: parseFloat(amount) || 0
            }
        }));
    };

    const saveEdit = (tenantId) => {
        setEditingInvoice(null);
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredHouses.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredHouses.length / itemsPerPage);

    // CSV Data
    const csvData = filteredHouses.map(h => ({
        Apartment: h.apartment,
        House: h.house_number,
        Tenant: h.tenant_name,
        Phone: h.phone_number,
        'Rent Amount': h.rent_amount ?? h.rent,
        Balance: h.balance
    }));

    if (isLoading) return <div className="p-4 text-center">Loading invoice data...</div>;
    if (error) return <div className="p-4 text-red-600 text-center">Error loading invoice data</div>;

    return (
        <div className="bg-white shadow-lg mt-5 p-3 rounded-lg max-w-8xl container-fluid">
            <h5 className='mt-2 font-semibold text-lg'>
                Generate Tenant Invoices {selectedMonth.charAt(0).toUpperCase() + selectedMonth.slice(1)} {selectedYear}
            </h5>

            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                    <CSVLink
                        data={csvData}
                        filename={`invoices-${selectedMonth}-${selectedYear}.csv`}
                        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-white text-decoration-none text-underline-none transition-colors"
                    >
                        <Download size={18} />
                        <span>Export</span>
                    </CSVLink>
                </div>

                {selectedTenants.length > 0 && (
                    <div className="flex justify-end">
                        <button
                            onClick={() => {
                                setSendSmsOnGenerate(false);
                                setShowModal(true);
                            }}
                            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 shadow-md px-5 py-2.5 rounded-lg text-white transition-colors"
                        >
                            <FileText size={18} />
                            <span>Generate ({selectedTenants.length}) Invoices</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
                <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-4 py-2 border border-gray-200 focus:border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                    {monthOptions.map((m) => (
                        <option key={m} value={m}>
                            {m.charAt(0).toUpperCase() + m.slice(1)}
                        </option>
                    ))}
                </select>
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="px-4 py-2 border border-gray-200 focus:border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                    {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                <input
                    type="text"
                    placeholder="Search unit / tenant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-4 py-2 border border-gray-200 focus:border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <select
                    value={apartmentFilter}
                    onChange={(e) => setApartmentFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-200 focus:border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">All Properties</option>
                    {uniqueApartments.map(apartment => (
                        <option key={apartment} value={apartment}>
                            {apartment}
                        </option>
                    ))}
                </select>
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={consolidateInvoices}
                        onChange={(e) => setConsolidateInvoices(e.target.checked)}
                        className="border-gray-300 rounded focus:ring-blue-500 w-4 h-4 text-blue-600"
                    />
                    <span>Consolidate utilities into rent</span>
                </label>
            </div>

            {/* Table */}
            <div className="bg-white shadow-sm mb-6 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="divide-y divide-gray-200 min-w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                                    <input
                                        type="checkbox"
                                        checked={
                                            filteredHouses.filter(item => buildInvoiceOptions(item).length > 0).length > 0 &&
                                            selectedTenants.length === filteredHouses.filter(item => buildInvoiceOptions(item).length > 0).length
                                        }
                                        onChange={handleSelectAll}
                                        className="border-gray-300 rounded focus:ring-blue-500 w-4 h-4 text-blue-600"
                                    />
                                </th>
                                <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Apartment</th>
                                <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Tenant Name</th>
                                <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Phone Number</th>
                                <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Invoice Type</th>
                                <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Amount</th>
                                <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentItems.length > 0 ? currentItems.map(h => (
                                <tr key={h.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {(() => {
                                            const hasOptions = buildInvoiceOptions(h).length > 0;
                                            return (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTenants.includes(h.id)}
                                                    onChange={() => handleTenantSelect(h.id)}
                                                    disabled={!hasOptions}
                                                    className="border-gray-300 rounded focus:ring-blue-500 w-4 h-4 text-blue-600"
                                                />
                                            );
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                                        <div className="flex flex-col">
                                            <span>{h.house_number}</span>
                                            <span className="text-gray-500 text-sm">{h.apartment}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-gray-700 whitespace-nowrap">{h.tenant_name}</td>
                                    <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{h.phone_number}</td>
                                    
                                    {/* Invoice Type */}
                                    <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                                        {(() => {
                                            const options = buildInvoiceOptions(h);
                                            if (consolidateInvoices && h.can_add_rent) {
                                                return <span className="text-gray-700">Consolidated</span>;
                                            }
                                            if (options.length === 0) {
                                                return <span className="text-gray-400">No invoice types</span>;
                                            }
                                            if (editingInvoice === h.id) {
                                                return (
                                                    <select
                                                        value={invoiceEdits[h.id]?.type || options[0].value}
                                                        onChange={(e) => handleInvoiceTypeChange(h.id, e.target.value, options)}
                                                        className="px-2 py-1 border border-gray-300 rounded"
                                                    >
                                                        {options.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                );
                                            }
                                            const currentType = invoiceEdits[h.id]?.type || options[0].value;
                                            return options.find(opt => opt.value === currentType)?.label || options[0].label;
                                        })()}
                                    </td>
                                    
                                    {/* Amount */}
                                    <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                                        {consolidateInvoices && h.can_add_rent ? (
                                            `KSH ${getConsolidatedAmount(h).toLocaleString()}`
                                        ) : editingInvoice === h.id ? (
                                            <input
                                                type="number"
                                                value={invoiceEdits[h.id]?.amount ?? h.rent_amount ?? h.rent ?? 0}
                                                onChange={(e) => handleAmountChange(h.id, e.target.value)}
                                                className="px-2 py-1 border border-gray-300 rounded w-24"
                                            />
                                        ) : (
                                            `KSH ${(invoiceEdits[h.id]?.amount ?? h.rent_amount ?? h.rent ?? 0).toLocaleString()}`
                                        )}
                                    </td>
                                    
                                    {/* Actions */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {editingInvoice === h.id ? (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => saveEdit(h.id)}
                                                    className="text-green-600 hover:text-green-800"
                                                >
                                                    <CheckCircle2 size={18} />
                                                </button>
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleEditInvoice(h.id)}
                                                className="text-blue-600 hover:text-blue-800"
                                            >
                                                <Edit size={18} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-gray-500 text-center">
                                        No pending invoices found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex md:flex-row flex-col justify-between items-center">
                <div className="mb-4 md:mb-0 text-gray-600 text-sm">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">
                        {Math.min(indexOfLastItem, filteredHouses.length)}
                    </span> of <span className="font-medium">{filteredHouses.length}</span> results
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        className={`flex items-center px-3 py-1.5 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
                    >
                        <ChevronLeft size={16} className="mr-1" />
                        Previous
                    </button>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className={`flex items-center px-3 py-1.5 rounded-md ${currentPage === totalPages || totalPages === 0 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
                    >
                        Next
                        <ChevronRight size={16} className="ml-1" />
                    </button>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
                    <div className="bg-white shadow-xl p-6 rounded-lg w-full max-w-md">
                        <div className="flex items-center space-x-3 mb-4">
                            <FileText className="text-blue-600" size={24} />
                            <h3 className="font-semibold text-gray-800 text-xl">Confirm Invoice Generation</h3>
                        </div>
                        <p className="mb-6 text-gray-600">
                            You're about to generate invoices for {selectedTenants.length} tenant(s). This action cannot be undone.
                        </p>

                        {/* Show edited invoice details in modal */}
                        <div className="mb-4 max-h-60 overflow-y-auto">
                            {filteredHouses
                                .filter(house => selectedTenants.includes(house.id))
                                .map(tenant => {
                                    const options = buildInvoiceOptions(tenant);
                                    const isConsolidated = consolidateInvoices && tenant.can_add_rent;
                                    const currentType = invoiceEdits[tenant.id]?.type || options[0]?.value;
                                    const currentLabel = isConsolidated
                                        ? 'Consolidated (Rent + Utilities)'
                                        : options.find(opt => opt.value === currentType)?.label || options[0]?.label || 'N/A';
                                    const currentAmount = isConsolidated
                                        ? getConsolidatedAmount(tenant)
                                        : (invoiceEdits[tenant.id]?.amount ?? tenant.rent_amount ?? tenant.rent ?? 0);
                                    return (
                                        <div key={tenant.id} className="mb-3 p-2 border-b">
                                            <div className="font-medium">{tenant.tenant_name}</div>
                                            <div className="text-gray-600 text-sm">
                                                Type: {currentLabel}
                                            </div>
                                            <div className="text-gray-600 text-sm">
                                                Amount: KSH {currentAmount.toLocaleString()}
                                            </div>
                                        </div>
                                    );
                                })
                            }
                        </div>

                        <div className="bg-slate-50 mb-4 p-3 border border-slate-200 rounded-md">
                            <label className={`flex items-start space-x-3 ${isGenerating ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                                <input
                                    type="checkbox"
                                    checked={sendSmsOnGenerate}
                                    onChange={(e) => setSendSmsOnGenerate(e.target.checked)}
                                    disabled={isGenerating}
                                    className="mt-1 border-gray-300 rounded focus:ring-blue-500 w-4 h-4 text-blue-600"
                                />
                                <div>
                                    <div className="font-medium text-gray-800">Send SMS after invoice generation</div>
                                    <p className="text-gray-500 text-sm">
                                        Tenants will receive an invoice download link after each invoice is created.
                                    </p>
                                </div>
                            </label>
                        </div>

                        {isGenerating && (
                            <div className="flex flex-col items-center mb-6">
                                <Loader2 className="mb-2 text-blue-600 animate-spin" size={32} />
                                <p className="text-gray-600">Generating invoices, please wait...</p>
                                <div className="bg-gray-200 mt-2 rounded-full w-full h-2.5">
                                    <div className="bg-blue-600 rounded-full h-2.5 animate-pulse" style={{ width: '70%' }}></div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setSendSmsOnGenerate(false);
                                }}
                                disabled={isGenerating}
                                className={`px-4 py-2 border rounded-lg transition-colors ${isGenerating
                                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                                    : 'hover:bg-gray-50 border-gray-300 text-gray-700'
                                    }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleGenerateInvoices}
                                disabled={isGenerating}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-white transition-colors ${isGenerating
                                    ? 'bg-blue-400'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {isGenerating ? (
                                    <Loader2 className="animate-spin" size={18} />
                                ) : (
                                    <CheckCircle2 size={18} />
                                )}
                                <span>{isGenerating ? 'Processing...' : 'Confirm'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCreditNoteModal && (
                <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
                    <div className="bg-white shadow-xl p-6 rounded-lg w-full max-w-md">
                        <div className="mb-4">
                            <h3 className="font-semibold text-gray-800 text-xl">Void Current KRA Invoice</h3>
                        </div>
                        <div className="space-y-3 text-sm">
                            <div><span className="font-medium">Invoice ID:</span> {selectedCreditInvoice?.id}</div>
                            <div><span className="font-medium">Tenant:</span> {selectedCreditInvoice?.tenant_name}</div>
                            <div><span className="font-medium">Unit:</span> {selectedCreditInvoice?.house_number}</div>
                            <div><span className="font-medium">Expected eTIMS Amount:</span> KSH {Number(selectedCreditInvoice?.expected_etims_amount || 0).toLocaleString()}</div>
                            <div><span className="font-medium">Base KRA Amount:</span> KSH {Number(selectedCreditInvoice?.kra_invoice_amount || 0).toLocaleString()}</div>
                            <div><span className="font-medium">Overstatement Notes Total:</span> KSH {Number(selectedCreditInvoice?.overstatement_credit_note_total || 0).toLocaleString()}</div>
                            <div><span className="font-medium">Net Posted After Notes:</span> KSH {Number(selectedCreditInvoice?.net_posted_etims_amount || selectedCreditInvoice?.kra_invoice_amount || 0).toLocaleString()}</div>
                            <div className="bg-amber-50 p-3 border border-amber-200 rounded-md text-amber-900">
                                This will void the current KRA invoice for KSH {Number(selectedCreditInvoice?.kra_invoice_amount || 0).toLocaleString()}. After that, generate a replacement invoice for KSH {Number(selectedCreditInvoice?.replacement_amount || selectedCreditInvoice?.expected_etims_amount || 0).toLocaleString()}.
                            </div>
                            {selectedCreditInvoice?.discrepancy_direction === 'understated' && (
                                <div className="text-amber-700">
                                    Current KRA invoice is understated. Void it, then reissue the correct invoice.
                                </div>
                            )}
                            {selectedCreditInvoice?.discrepancy_direction === 'overstated' && (
                                <div className="text-rose-700">
                                    Current KRA invoice is overstated. Void it, then reissue the correct invoice.
                                </div>
                            )}
                            <div>
                                <label className="block mb-1 font-medium text-gray-700">Adjustment Type</label>
                                <select
                                    value={adjustmentType}
                                    onChange={(e) => setAdjustmentType(e.target.value)}
                                    className="bg-gray-100 w-full px-3 py-2 border border-gray-300 rounded-md"
                                    disabled
                                >
                                    <option value="overstatement">Overstatement</option>
                                </select>
                            </div>
                            <div>
                                <label className="block mb-1 font-medium text-gray-700">Void Amount</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={creditAmount}
                                    onChange={(e) => setCreditAmount(e.target.value)}
                                    className="bg-gray-100 w-full px-3 py-2 border border-gray-300 rounded-md"
                                    disabled
                                />
                            </div>
                            <div>
                                <label className="block mb-1 font-medium text-gray-700">Reason Code</label>
                                <select
                                    value={creditReasonCode}
                                    onChange={(e) => setCreditReasonCode(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    {creditReasonOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {creditNoteResult && (
                                <div className="bg-gray-50 mt-2 p-3 border rounded-md">
                                    <div className="font-semibold text-gray-800 text-sm">Credit Note Response</div>
                                    <div className="mt-2 text-gray-700 text-xs">
                                        Created: {creditNoteResult?.created_count ?? creditNoteResult?.created?.length ?? 0}
                                    </div>
                                    <div className="text-gray-700 text-xs">
                                        Failed: {creditNoteResult?.failed_count ?? creditNoteResult?.failed?.length ?? 0}
                                    </div>

                                    {(creditNoteResult?.created || []).length > 0 && (
                                        <div className="space-y-1 mt-2 text-xs">
                                            {creditNoteResult.created.map((item, idx) => (
                                                <div key={`${item?.credit_note_id || 'created'}-${idx}`} className="bg-white p-2 border rounded">
                                                    <div>Credit Note #: {item?.credit_invoice_number || 'N/A'}</div>
                                                    <div>Original Invoice #: {item?.original_invoice_number || 'N/A'}</div>
                                                    <div>Amount: KSH {Number(item?.total_amount || 0).toLocaleString()}</div>
                                                    <div>Status: Current invoice voided</div>
                                                    <div>QR: {item?.qrCode ? 'Available' : 'Not returned'}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {(creditNoteResult?.failed || []).length > 0 && (
                                        <div className="space-y-1 mt-2 text-xs">
                                            {creditNoteResult.failed.map((item, idx) => (
                                                <div key={`${item?.invoice_id || 'failed'}-${idx}`} className="bg-rose-50 p-2 border border-rose-200 rounded text-rose-700">
                                                    Invoice {item?.invoice_id || 'N/A'}: {item?.error || 'Unknown error'}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowCreditNoteModal(false);
                                    setCreditNoteResult(null);
                                }}
                                disabled={isPostingCreditNote}
                                className={`px-4 py-2 border rounded-lg transition-colors ${isPostingCreditNote
                                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                                    : 'hover:bg-gray-50 border-gray-300 text-gray-700'
                                    }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateCreditNote}
                                disabled={isPostingCreditNote}
                                className={`px-4 py-2 rounded-lg text-white transition-colors ${isPostingCreditNote
                                    ? 'bg-blue-400'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {isPostingCreditNote ? 'Voiding...' : 'Void Current Invoice'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showInvoiceSmsModal && (
                <div
                    className="z-50 fixed inset-0 overflow-y-auto bg-black/50 p-4"
                    onClick={() => {
                        if (!isSendingInvoiceSms) {
                            setShowInvoiceSmsModal(false);
                        }
                    }}
                >
                    <div className="flex justify-center items-center min-h-full">
                        <div
                            className="bg-white shadow-xl rounded-lg w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-start gap-4 px-6 py-4 border-b">
                                <div>
                                    <div className="flex items-center space-x-3 mb-2">
                                        <Send className="text-blue-600" size={22} />
                                        <h3 className="font-semibold text-gray-800 text-xl">Confirm Invoice SMS</h3>
                                    </div>
                                    <p className="text-gray-600">
                                        You&apos;re about to send invoice SMS notifications for {selectedExistingInvoiceRows.length} generated invoice(s).
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowInvoiceSmsModal(false)}
                                    disabled={isSendingInvoiceSms}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label="Close SMS preview"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="flex sm:flex-row flex-col justify-between items-center gap-3 px-6 py-3 border-b bg-slate-50">
                                <p className="text-slate-600 text-sm">
                                    Review the messages below, or send now without scrolling to the bottom.
                                </p>
                                <button
                                    onClick={handleSendExistingInvoiceSms}
                                    disabled={isSendingInvoiceSms}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-white transition-colors ${isSendingInvoiceSms
                                        ? 'bg-blue-400'
                                        : 'bg-blue-600 hover:bg-blue-700'
                                        }`}
                                >
                                    {isSendingInvoiceSms ? (
                                        <Loader2 className="animate-spin" size={18} />
                                    ) : (
                                        <Send size={18} />
                                    )}
                                    <span>{isSendingInvoiceSms ? 'Sending...' : 'Confirm And Send SMS'}</span>
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                <div className="space-y-3 pr-1">
                            {selectedExistingInvoiceRows.map((invoice) => (
                                <div key={invoice.id} className="p-3 border rounded-lg">
                                    <div className="flex sm:flex-row flex-col sm:justify-between sm:items-start gap-2 mb-3">
                                        <div>
                                            <div className="font-medium text-gray-900">{invoice.tenant_name}</div>
                                            <div className="text-gray-500 text-sm">{invoice.tenant_phone}</div>
                                        </div>
                                        <div className="text-gray-500 text-sm sm:text-right">
                                            <div>{invoice.house_number} · {invoice.apartment_name}</div>
                                            <div>
                                                {formatInvoiceMonth(invoice.month)} {invoice.year} · {invoice.reference === 'expense' ? (invoice.expense_name || 'Expense') : invoice.reference}
                                            </div>
                                            <div>KSH {Number(invoice.amount || 0).toLocaleString()}</div>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 p-3 border rounded-md text-slate-700 text-sm whitespace-pre-line">
                                        {buildInvoiceSmsPreview(invoice)}
                                    </div>
                                </div>
                            ))}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-white">
                            <button
                                onClick={() => setShowInvoiceSmsModal(false)}
                                disabled={isSendingInvoiceSms}
                                className={`px-4 py-2 border rounded-lg transition-colors ${isSendingInvoiceSms
                                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                                    : 'hover:bg-gray-50 border-gray-300 text-gray-700'
                                    }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSendExistingInvoiceSms}
                                disabled={isSendingInvoiceSms}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-white transition-colors ${isSendingInvoiceSms
                                    ? 'bg-blue-400'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {isSendingInvoiceSms ? (
                                    <Loader2 className="animate-spin" size={18} />
                                ) : (
                                    <Send size={18} />
                                )}
                                <span>{isSendingInvoiceSms ? 'Sending...' : 'Send SMS'}</span>
                            </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Existing invoices list */}
            <div className="bg-white shadow-lg mt-8 p-3 rounded-lg max-w-8xl container-fluid">
                <div className="flex flex-wrap justify-between items-center mb-4">
                    <h5 className="font-semibold text-lg">
                        Existing Invoices {selectedMonth.charAt(0).toUpperCase() + selectedMonth.slice(1)} {selectedYear}
                    </h5>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-gray-500 text-sm">
                            Selected: {selectedExistingInvoices.length}
                        </span>
                        <button
                            onClick={() => handleOpenInvoiceSmsModal()}
                            disabled={selectedExistingInvoices.length === 0}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-white transition-colors ${selectedExistingInvoices.length === 0
                                ? 'bg-blue-300 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                        >
                            <Send size={16} />
                            <span>{selectedExistingInvoices.length > 0 ? `Send SMS (${selectedExistingInvoices.length})` : 'Send SMS'}</span>
                        </button>
                        <select
                            value={invoiceVarianceFilter}
                            onChange={(e) => setInvoiceVarianceFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-200 rounded-lg"
                        >
                            <option value="all">All</option>
                            <option value="understated">Understated</option>
                            <option value="overstated">Overstated</option>
                            <option value="balanced">Balanced</option>
                            <option value="no_kra">No KRA</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Search invoices..."
                            value={invoiceSearchTerm}
                            onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                            className="px-4 py-2 border border-gray-200 focus:border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {isInvoicesLoading ? (
                    <div className="p-4 text-center">Loading invoices...</div>
                ) : invoicesError ? (
                    <div className="p-4 text-red-600 text-center">Error loading invoices</div>
                ) : (
                    <div className="bg-white shadow-sm rounded-xl overflow-hidden">
                        <div className="bg-slate-50 px-4 py-3 border-b text-slate-600 text-sm">
                            Use the first column to select existing invoices for SMS. Only invoices with a tenant phone number can be selected.
                        </div>
                        <div className="overflow-x-auto">
                            <table className="divide-y divide-gray-200 min-w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                                            <input
                                                type="checkbox"
                                                checked={allFilteredInvoicesSelected}
                                                onChange={handleSelectAllExistingInvoices}
                                                disabled={selectableFilteredInvoiceIds.length === 0}
                                                className="border-gray-300 rounded focus:ring-blue-500 w-4 h-4 text-blue-600"
                                            />
                                        </th>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">House</th>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Tenant</th>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Phone</th>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Amount</th>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {invoiceCurrentItems.length > 0 ? (
                                        invoiceCurrentItems.map(inv => (
                                            <tr key={inv.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedExistingInvoices.includes(inv.id)}
                                                        onChange={() => handleExistingInvoiceSelect(inv.id)}
                                                        disabled={!canSendInvoiceSms(inv)}
                                                        title={canSendInvoiceSms(inv) ? 'Select invoice for SMS' : 'Tenant phone number is missing'}
                                                        className="border-gray-300 rounded focus:ring-blue-500 w-4 h-4 text-blue-600"
                                                    />
                                                </td>
                                                <td className="px-6 py-4 text-gray-900 whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span>{inv.house_number}</span>
                                                        <span className="text-gray-500 text-sm">{inv.apartment_name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-700 whitespace-nowrap">{inv.tenant_name}</td>
                                                <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{inv.tenant_phone}</td>
                                                <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                                                    {inv.reference === 'expense'
                                                        ? `${inv.expense_name || 'Expense'}`
                                                        : inv.reference}
                                                </td>
                                                <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span>KSH {Number(inv.amount || 0).toLocaleString()}</span>
                                                        {renderInvoiceVariance(inv)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{inv.status}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        {canSendInvoiceSms(inv) && (
                                                            <button
                                                                onClick={() => handleOpenInvoiceSmsModal([inv.id])}
                                                                className="bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md text-white transition-colors"
                                                            >
                                                                Send SMS
                                                            </button>
                                                        )}
                                                        {inv.reference === 'rent' && inv.has_kra_invoice && !inv.is_voided && inv.requires_void_and_reissue && (
                                                            <button
                                                                onClick={() => openCreditNoteModal(inv)}
                                                                className="text-blue-600 hover:text-blue-800"
                                                            >
                                                                Void With Credit Note
                                                            </button>
                                                        )}
                                                        {inv.reference === 'rent' && inv.has_kra_invoice && inv.is_voided && (
                                                            <button
                                                                onClick={() => handleDeleteKraInvoice(inv.id)}
                                                                className="text-amber-700 hover:text-amber-900"
                                                            >
                                                                Delete KRA Invoice
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleDeleteInvoice(inv.id)}
                                                            className="text-red-600 hover:text-red-800"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-4 text-gray-500 text-center">
                                                No invoices found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex md:flex-row flex-col justify-between items-center p-3 border-t">
                            <div className="mb-2 md:mb-0 text-gray-600 text-sm">
                                Showing <span className="font-medium">{filteredInvoiceRows.length === 0 ? 0 : invoiceIndexOfFirst + 1}</span> to <span className="font-medium">
                                    {Math.min(invoiceIndexOfLast, filteredInvoiceRows.length)}
                                </span> of <span className="font-medium">{filteredInvoiceRows.length}</span> results
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => setInvoicePage(p => Math.max(p - 1, 1))}
                                    disabled={invoicePage === 1}
                                    className={`flex items-center px-3 py-1.5 rounded-md ${invoicePage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
                                >
                                    <ChevronLeft size={16} className="mr-1" />
                                    Previous
                                </button>
                                <button
                                    onClick={() => setInvoicePage(p => Math.min(p + 1, invoiceTotalPages))}
                                    disabled={invoicePage === invoiceTotalPages || invoiceTotalPages === 0}
                                    className={`flex items-center px-3 py-1.5 rounded-md ${invoicePage === invoiceTotalPages || invoiceTotalPages === 0 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
                                >
                                    Next
                                    <ChevronRight size={16} className="ml-1" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
